import { GalleryImage } from './GalleryImage';
import { Box, makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import useSize from '@react-hook/size';
import Carousel from 'nuka-carousel';
import React, { Fragment, useCallback, useMemo, useRef } from 'react';
import AspectRatio from 'react-aspect-ratio';
import { ExportInterface } from 'react-images-uploading/dist/typings';
import { GalleryArrow, GalleryArrowDirection } from './GalleryArrow';
import { ButtonColor, ButtonWithIcon } from '../Button/ButtonWithIcon';
import { Icon } from '../Icon/Icon';
import { Text, TextTypography } from '../Text/Text';
import { ReactComponent as PlusIcon } from 'assets/icons/main-16/plus--16.svg';
import i18n from '../../../i18n';
import { getCloudinaryImageSrc } from 'core/utils/utils';

interface GalleryStyleParams {
    carouselContainerWidth: number;
    slidesToShow: number;
    imageCount: number;
}

const MARGIN = 32;
const OFFSET = 8;

const useStyles = makeStyles({
    carouselContainer: {
        display: 'flex',
    },
    carousel: {
        height: '100%',
        paddingLeft: (params: GalleryStyleParams) => {
            if (params.slidesToShow === 1) {
                return 0;
            }
            return params.imageCount > params.slidesToShow ? OFFSET : 0;
        },
        paddingRight: (params: GalleryStyleParams) => {
            if (params.slidesToShow === 1) {
                return 0;
            }
            return params.imageCount > params.slidesToShow ? MARGIN - OFFSET : MARGIN;
        },
        width: (params: GalleryStyleParams) => {
            const totalWidth = params.carouselContainerWidth;
            if (params.slidesToShow === 1) {
                return totalWidth;
            }
            return (params.slidesToShow / (params.slidesToShow + 1)) * (totalWidth - MARGIN) + MARGIN;
        },
    },
    addImageButtonContainer: {
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: (params: GalleryStyleParams) => {
            return (1 / (params.slidesToShow + 1)) * (params.carouselContainerWidth - MARGIN);
        },
    },
    addImageButton: {
        alignItems: 'center',
        backgroundColor: '#eeeeee',
        borderRadius: 20,
        border: '2px dashed rgba(36, 49, 67, 0.2)',
        color: '#aaa',
        cursor: 'pointer',
        display: 'flex',
        fontSize: 32,
        height: '100%',
        justifyContent: 'center',
        width: '100%',
        transition: '0.2s ease background-color',
        '&:hover': {
            backgroundColor: '#e5e5e5',
        },
    },
    button: {
        paddingTop: 12,
        paddingRight: 50,
        paddingBottom: 12,
        paddingLeft: 45,
    },
});

export interface GalleryContentsProps extends ExportInterface {
    onPreviewRef: (ref: HTMLDivElement, index: number) => void;
    onEdit: (index: number) => void;
    aspectRatio?: number;
    cloudinaryImages?: boolean;
}

export const GalleryContents = (props: GalleryContentsProps) => {
    const { imageList, onImageUpload, onImageRemove, dragProps, onEdit, onPreviewRef, aspectRatio = 2, cloudinaryImages = true } = props;

    const { t } = i18n.useTranslation();
    const carouselContainerRef = useRef<HTMLDivElement>();
    const [carouselContainerWidth] = useSize(carouselContainerRef);

    const imageCount = imageList.length;

    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up(708));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(380));

    const slidesToShow = useMemo(() => {
        if (isLargeScreen) {
            return 3;
        }
        if (isSmallScreen) {
            return 1;
        }
        return 2;
    }, [isLargeScreen, isSmallScreen]);

    const classes = useStyles({ carouselContainerWidth, slidesToShow, imageCount });

    const prevArrow = useCallback(
        ({ previousSlide }) => {
            return imageCount > slidesToShow && slidesToShow > 1 ? (
                <GalleryArrow type={GalleryArrowDirection.PREV} onClick={previousSlide} />
            ) : null;
        },
        [imageCount, slidesToShow],
    );

    const nextArrow = useCallback(
        ({ nextSlide }) => {
            return imageCount > slidesToShow && slidesToShow > 1 ? (
                <GalleryArrow type={GalleryArrowDirection.NEXT} onClick={nextSlide} />
            ) : null;
        },
        [imageCount, slidesToShow],
    );

    const dots = useMemo(() => {
        return imageCount > slidesToShow && slidesToShow === 1 ? undefined : () => null;
    }, [imageCount, slidesToShow]);

    return (
        <Fragment>
            <div ref={carouselContainerRef} className={classes.carouselContainer}>
                {imageCount > 0 && (
                    <Box flex="1">
                        <div className={classes.carousel}>
                            <Carousel
                                wrapAround={true}
                                autoplay={false}
                                slidesToShow={slidesToShow}
                                swiping={true}
                                renderCenterLeftControls={prevArrow}
                                renderCenterRightControls={nextArrow}
                                renderBottomCenterControls={dots}
                            >
                                {imageList.map((image, index) => {
                                    let src = cloudinaryImages ? getCloudinaryImageSrc({ src: image.imageId, width: 360 }) : `/api/images/${image.imageId}`;
                                    if (image.dataURL) {
                                        src = image.dataURL;
                                    }
                                    return (
                                        <GalleryImage
                                            key={index}
                                            previewRef={onPreviewRef}
                                            index={index}
                                            aspectRatio={aspectRatio}
                                            src={src}
                                            onEdit={onEdit}
                                            onDelete={onImageRemove}
                                            canEdit={!image.imageId}
                                        />
                                    );
                                })}
                            </Carousel>
                        </div>
                    </Box>
                )}

                {!isSmallScreen && (
                    <div className={classes.addImageButtonContainer}>
                        <Box p={0.5}>
                            <AspectRatio ratio={aspectRatio}>
                                <div className={classes.addImageButton} onClick={onImageUpload} {...dragProps}>
                                    +
                                </div>
                            </AspectRatio>
                        </Box>
                    </div>
                )}
            </div>

            {isSmallScreen && (
                <Box mt={2} display="flex" justifyContent="center">
                    <ButtonWithIcon
                        onClick={onImageUpload}
                        icon={<Icon component={PlusIcon} size={14} svgSize={14} color="#fff" />}
                        color={ButtonColor.GREEN}
                        className={classes.button}
                    >
                        <Text typography={TextTypography.BODY_ACCENT_WHITE}> {t('common:gallery.add')}</Text>
                    </ButtonWithIcon>
                </Box>
            )}
        </Fragment>
    );
};
